/**
 * Each page types defines here belows
 */
type HomePage = boolean;

interface TransferPage {
  anipay: boolean;
  payment_acc2acc: boolean;
  payment_interbank: boolean;
  payment_local_transfer: boolean;
  payment_foreign_transfer: boolean;
  payment_conversion: boolean;
}

export interface PaymentPage {
  HOP: boolean;
  payment_budget: boolean;
  payment_edv: boolean;
  other_payments: boolean;
  preflight_fee: boolean;
}

interface SalaryList {
  'employee-table': boolean;
  'new-payments': boolean;
  'payments-history': boolean;
}

interface SalaryCard {
  salary_card: boolean;
  salary_cards_prolong: boolean;
  salary_cards_statuses: boolean;
}

interface Guarantee {
  'guarantee-v2': boolean;
  loan_list: boolean;
}

interface Loan {
  request_loan: boolean;
  use_loan_limit: boolean;
}

export interface PermissionMapper {
  account_list: boolean;
  'home-page': HomePage;
  'signed-operations': boolean;
  'payment-orders': boolean;
  templates: boolean;
  settings: boolean;
  'transfer-page': TransferPage;
  'payment-page': PaymentPage;
  salary_list: SalaryList;
  'salary-card': SalaryCard;
  guarantee: Guarantee;
  loan: Loan;
}
type nestedLists = [
  'transfer_list',
  'payment_list',
  'salary_list',
  'salary_card',
  'guarantee',
  'loan',
];

// Page ids whose permission value is an object of sub-flags (e.g. TransferPage,
// PaymentPage), as opposed to a single boolean like 'home-page'. Only these
// can be the target of a nested list — adding a new object-shaped page to
// PermissionMapper makes it eligible here automatically.
type ObjectPageKey = {
  [K in keyof PermissionMapper]: PermissionMapper[K] extends boolean ? never : K;
}[keyof PermissionMapper];

// A nested item's id is scoped to whichever object page it rolls up into
// (e.g. transfer-page's payment types), not the top-level page id. This is
// the union of every object page's own keys, so new pages are picked up
// without touching this type.
interface NestedMenuItem {
  id: { [K in ObjectPageKey]: keyof PermissionMapper[K] }[ObjectPageKey];
  is_guest: boolean;
}

interface MenuList extends Partial<Record<nestedLists[number], NestedMenuItem[]>> {
  id: keyof PermissionMapper;
  is_guest: boolean;
}

// Maps each nested-list key found on a MenuList item to the object page it
// rolls up into. To wire up a new nested page: add it to PermissionMapper
// above, then point its nested-list key(s) here — no other changes needed.
const nestedListObjects: Record<nestedLists[number], ObjectPageKey> = {
  transfer_list: 'transfer-page',
  payment_list: 'payment-page',
  salary_list: 'salary_list',
  salary_card: 'salary-card',
  guarantee: 'guarantee',
  loan: 'loan',
};

export const buildPermissionMapper = (menuList: MenuList[]): PermissionMapper => {
  const mapperObj = {} as PermissionMapper;

  menuList.forEach(item => {
    const keys = Object.keys(item); // ['transfer-list' , 'is_guest'];

    const nestedListKey = keys.find(key => nestedListObjects[key as nestedLists[number]]) as
      | nestedLists[number]
      | undefined;
    const nestedList = nestedListKey ? (item[nestedListKey] ?? null) : null;

    if (nestedList) {
      const permissionKey = nestedListObjects[nestedListKey!];
      // Same union-key write limitation as the `item.id` cast below: TS
      // can't narrow through a generic ObjectPageKey, so the target type
      // resolves to an intersection of every object page's shape.
      const pagePermissions = (mapperObj[permissionKey] ??= {} as any);

      nestedList.forEach(nestedItem => {
        if (Object.keys(nestedItem).includes('is_guest')) {
          pagePermissions[nestedItem.id] = !nestedItem.is_guest;
        }
      });
    }

    if (keys.includes('is_guest')) {
      // TS can't narrow a write through a union-typed key (item.id), so the
      // assignment target resolves to an intersection of every possible
      // value type. Cast through `any` rather than fabricate a fake-safe type.
      mapperObj[item.id] = !item.is_guest as any;
    }
  });

  return mapperObj;
};
